import React, { useContext, useEffect, useMemo, useState } from "react";
import vendorAPI from "../api/vendorAPI";
import { useVendorTerms } from "../utils/terminology";
import Button from "./Button";
import useGuardedMutation from "../hooks/useGuardedMutation";
import { AuthContext } from "../context/AuthContext";

// Mirrors Vendor.tinBranch on the backend: 4 or 5 digits.
const TIN_BRANCH_RE = /^\d{4,5}$/;

const tokenInput =
  "w-full border border-ink-line rounded-md px-3 py-2 text-sm text-ink bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-primary-500";
const tokenLabel = "block text-xs font-medium text-ink mb-1";

const composeIndividualName = (last, first, middle) =>
  [last.trim(), [first.trim(), middle.trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");

const getFriendlyErrorMessage = (error, fallback = "Something went wrong.") => {
  const raw =
    (typeof error === "string" && error) ||
    error?.backend?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

  return String(raw)
    .replace(/^ValidationError:\s*/i, "")
    .replace(/^ValidatorError:\s*/i, "")
    .trim();
};

const getErrorField = (message = "") => {
  const normalized = String(message).toLowerCase();
  if (normalized.includes("vendor name") || normalized.includes("name already exists")) return "name";
  // Branch-code errors are shown in the banner, not under the TIN input.
  if (normalized.includes("branch")) return "general";
  if (normalized.includes("tin")) return "tin";
  return "general";
};

const onlyDigits = (s = "") => (s || "").replace(/\D/g, "");

const parseVendorAddress = (address) => {
  if (!address) return { street: "", city: "", province: "", zip: "" };

  // If address is a plain string (some vendors may have this)
  if (typeof address === "string") {
    // Try to split comma-separated: "Street, City, Province ZIP"
    const parts = address.split(",").map(p => p.trim()).filter(Boolean);
    if (parts.length >= 3) {
      return {
        street: parts.slice(0, -2).join(", "),
        city: parts[parts.length - 2],
        province: parts[parts.length - 1],
        zip: "",
      };
    } else if (parts.length === 2) {
      return { street: "", city: parts[0], province: parts[1], zip: "" };
    }
    return { street: address, city: "", province: "", zip: "" };
  }

  // If address is a structured object
  if (typeof address === "object") {
    const street = [
      address.building,
      address.street,
      address.substreet,
      address.barangay,
    ].filter(Boolean).join(", ");

    return {
      street: street || "",
      city: address.city || address.cityMunicipality || address.district || "",
      province: address.province || "",
      zip: address.zipCode || address.zip || "",
    };
  }

  return { street: "", city: "", province: "", zip: "" };
};

const formatTinDisplay = (tinRaw) => {
  const digits = onlyDigits(tinRaw);
  // Auto-format when 10 or more digits: XXX-XXX-XXX-(remainder)
  if (digits.length > 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  return tinRaw;
};

const VendorForm = ({ 
  initialVendor = null,
  initialName = "",
  onSave, 
  onCancel, 
  saving = false, 
  showButtons = true 
}) => {
  const term = useVendorTerms();
  const isEditMode = Boolean(initialVendor?._id);
  const [name, setName] = useState(initialName);
  const [tin, setTin] = useState("");
  const [code, setCode] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [addressStreet, setAddressStreet] = useState("");
  const [addressCity, setAddressCity] = useState("");
  const [addressProvince, setAddressProvince] = useState("");
  const [addressZip, setAddressZip] = useState("");
  const [vendorType, setVendorType] = useState("supplier");
  const [businessType, setBusinessType] = useState("corporation");
  const [status, setStatus] = useState("active");
  const [defaultAtcCode2306, setDefaultAtcCode2306] = useState("WI010");
  const [defaultWithholdingRate2306, setDefaultWithholdingRate2306] = useState(1);

  // WHT payee fields (§3). Individuals use the name split; everyone else the
  // registered name. Unset fields stay unset — the backend resolves them at
  // read time (backend/utils/payeeProfile.js).
  const [registeredName, setRegisteredName] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [tinBranch, setTinBranch] = useState("");
  const [isVatRegistered, setIsVatRegistered] = useState(false);
  const [hasDeclaration, setHasDeclaration] = useState(false);
  const [belowThreshold, setBelowThreshold] = useState(false);
  const [declarationYear, setDeclarationYear] = useState("");
  const [declarationDate, setDeclarationDate] = useState("");

  // Save goes through the guard: blocks double submits and stamps an
  // Idempotency-Key on the create/update request (CLAUDE.md).
  const { mutate: guardedSave, isPending } = useGuardedMutation(onSave);
  const busy = saving || isPending;

  // WHT payee fields are an accounting-firm feature. Every other tenant type
  // gets the form exactly as before: no fields, no validation, no payload keys.
  // Read from the context directly (not useAuth, which throws without a
  // provider), so a standalone render falls back to the old form.
  const auth = useContext(AuthContext);
  const showPayeeFields = auth?.user?.tenantType === "accounting_firm";
  const isIndividual = showPayeeFields && businessType === "individual";

  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState({ field: "", message: "" });

  useEffect(() => {
    if (initialVendor) {
      setName(initialVendor.name || "");
      setTin(initialVendor.tin || "");
      setCode(initialVendor.code || "");
      setContactInfo(initialVendor.contactInfo || "");
      
      // Parse structured address object
      const addr = parseVendorAddress(initialVendor.address);
      setAddressStreet(addr.street);
      setAddressCity(addr.city);
      setAddressProvince(addr.province);
      setAddressZip(addr.zip);
      
      setVendorType(initialVendor.vendorType || "supplier");
      setBusinessType(initialVendor.businessType || "corporation");
      setStatus(initialVendor.status || "active");
      setDefaultAtcCode2306(initialVendor.defaultAtcCode2306 || "WI010");
      setDefaultWithholdingRate2306(initialVendor.defaultWithholdingRate2306 ?? 1);

      setRegisteredName(initialVendor.registeredName || "");
      setLastName(initialVendor.lastName || "");
      setFirstName(initialVendor.firstName || "");
      setMiddleName(initialVendor.middleName || "");
      setTinBranch(initialVendor.tinBranch || "");
      setIsVatRegistered(Boolean(initialVendor.isVatRegistered));
      const sd = initialVendor.swornDeclaration || {};
      setHasDeclaration(Boolean(sd.hasDeclaration));
      setBelowThreshold(Boolean(sd.belowThreshold));
      setDeclarationYear(sd.taxYear ? String(sd.taxYear) : "");
      setDeclarationDate(sd.dateReceived ? String(sd.dateReceived).slice(0, 10) : "");
    }
  }, [initialVendor]);

  const tinDigits = useMemo(() => onlyDigits(tin), [tin]);

  const validate = () => {
    const composedName = isIndividual ? composeIndividualName(lastName, firstName, middleName) : "";
    if (!name.trim() && !composedName) return `${term.one} name is required.`;

    if (showPayeeFields && tinBranch && !TIN_BRANCH_RE.test(tinBranch)) {
      return "TIN branch code must be 4 or 5 digits (e.g. 0000 or 00000 for head office).";
    }

    if (showPayeeFields && hasDeclaration) {
      const year = Number(declarationYear);
      if (!Number.isInteger(year) || year < 2000 || year > 2100) {
        return "Enter the tax year the sworn declaration covers.";
      }
    }

    if (tinDigits.length > 0 && tinDigits.length <= 9) {
      return "TIN must be more than 9 digits (10-12 digits).";
    }

    if (tinDigits.length > 0 && tinDigits.length > 12) {
      return "TIN too long. Please use 10-12 digits.";
    }

    const rateNum = Number(defaultWithholdingRate2306);
    if (Number.isNaN(rateNum) || rateNum < 0 || rateNum > 100) {
      return "Default withholding rate must be between 0 and 100.";
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const msg = validate();
    if (msg) {
      setError(msg);
      setFieldError({ field: getErrorField(msg), message: msg });
      return;
    }

    const payload = {
      // An individual entered only by name split gets "LAST, FIRST MIDDLE".
      name: name.trim() || (isIndividual ? composeIndividualName(lastName, firstName, middleName) : ""),
      tin: tinDigits || "", // Store only digits, no dashes
      code: code.trim() || undefined,
      contactInfo: contactInfo.trim() || undefined,
      // Build structured address object
      address: {
        street: addressStreet.trim() || undefined,
        city: addressCity.trim() || undefined,
        province: addressProvince.trim() || undefined,
        zipCode: addressZip.trim() || undefined,
      },
      vendorType,
      businessType,
      status,
      defaultAtcCode2306: defaultAtcCode2306.trim() || "WI010",
      defaultWithholdingRate2306: Number(defaultWithholdingRate2306) || 1,

      // WHT payee fields (accounting firms only). Only the name set that
      // applies to the business type is sent; '' clears tinBranch.
      ...(showPayeeFields ? {
        ...(isIndividual
          ? {
              lastName: lastName.trim() || undefined,
              firstName: firstName.trim() || undefined,
              middleName: middleName.trim() || undefined,
            }
          : { registeredName: registeredName.trim() || undefined }),
        tinBranch: tinBranch.trim(),
        isVatRegistered,
        swornDeclaration: hasDeclaration
          ? {
              hasDeclaration: true,
              belowThreshold,
              taxYear: Number(declarationYear),
              dateReceived: declarationDate || undefined,
            }
          : { hasDeclaration: false, belowThreshold: false },
      } : {}),
    };

    try {
      setError("");
      setFieldError({ field: "", message: "" });
      await guardedSave(payload);
    } catch (e) {
      console.error(`${isEditMode ? "Update" : "Create"} vendor failed:`, e);
      const friendly = getFriendlyErrorMessage(e, `Failed to ${isEditMode ? "update" : "create"} vendor.`);
      setError(friendly);
      setFieldError({ field: getErrorField(friendly), message: friendly });
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">{term.one} Name *</label>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (fieldError.field === "name") {
                setFieldError({ field: "", message: "" });
                setError("");
              }
            }}
            className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 ${
              fieldError.field === "name" ? "border-red-500 bg-red-50" : "border-gray-300"
            }`}
            placeholder="e.g. ABC Trading / John Dela Cruz"
            disabled={busy}
          />
          {fieldError.field === "name" && (
            <p className="text-xs text-red-600 mt-1 font-semibold">✕ {fieldError.message}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">TIN (optional)</label>
          <input
            value={formatTinDisplay(tin)}
            onChange={(e) => {
              const rawValue = onlyDigits(e.target.value);
              setTin(rawValue);
              if (fieldError.field === "tin") {
                setFieldError({ field: "", message: "" });
                setError("");
              }
            }}
            className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 ${
              fieldError.field === "tin" ? "border-red-500 bg-red-50" : "border-gray-300"
            }`}
            placeholder="000-000-000-000"
            disabled={busy}
          />
          <div className="text-[11px] text-gray-500 mt-1">
            Digits detected: {tinDigits.length || 0}
          </div>
          {fieldError.field === "tin" && (
            <p className="text-xs text-red-600 mt-1 font-semibold">✕ {fieldError.message}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{term.one} Code (optional)</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Leave blank to auto-generate"
            disabled={busy}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Contact Info (optional)</label>
          <input
            value={contactInfo}
            onChange={(e) => setContactInfo(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Phone, email, contact person, etc."
            disabled={busy}
          />
        </div>

        {/* ADDRESS SECTION */}
        <div className="md:col-span-2 border-t pt-4 mt-2">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Address
            <span className="text-xs font-normal text-gray-400 ml-2">
              (Required for BIR RELIEF DAT export)
            </span>
          </h4>
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Street / Barangay
          </label>
          <input
            value={addressStreet}
            onChange={(e) => setAddressStreet(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            placeholder="e.g. 123 Main St, Brgy Centro"
            disabled={busy}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            City / Municipality
          </label>
          <input
            value={addressCity}
            onChange={(e) => setAddressCity(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            placeholder="e.g. ILOILO CITY"
            disabled={busy}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Province
          </label>
          <input
            value={addressProvince}
            onChange={(e) => setAddressProvince(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            placeholder="e.g. ILOILO"
            disabled={busy}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Zip Code
          </label>
          <input
            value={addressZip}
            onChange={(e) => setAddressZip(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            placeholder="e.g. 5000"
            disabled={busy}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{term.one} Type</label>
          <select
            value={vendorType}
            onChange={(e) => setVendorType(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
            disabled={busy}
          >
            {vendorAPI.getVendorTypes().map((v) => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Business Type</label>
          <select
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
            disabled={busy}
          >
            {vendorAPI.getBusinessTypes().map((v) => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
            disabled={busy}
          >
            {vendorAPI.getStatusOptions().map((v) => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Default ATC (2306)</label>
          <input
            value={defaultAtcCode2306}
            onChange={(e) => setDefaultAtcCode2306(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            disabled={busy}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Default Withholding Rate (2306)</label>
          <input
            type="number"
            value={defaultWithholdingRate2306}
            onChange={(e) => setDefaultWithholdingRate2306(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            min="0"
            max="100"
            step="0.01"
            disabled={busy}
          />
        </div>

        {/* WHT PAYEE DETAILS (DESIGN.md tokens) — accounting firms only */}
        {showPayeeFields && (
        <section aria-labelledby="payee-heading" className="md:col-span-2 border-t border-ink-line pt-4 mt-2">
          <h4 id="payee-heading" className="text-sm font-medium text-ink">Withholding tax payee details</h4>
          <p className="text-xs text-ink-body mt-1 mb-3">
            Used on BIR Form 2307, 1601-EQ and the QAP. {isIndividual
              ? "Individuals are reported by last, first and middle name."
              : "Non-individuals are reported by their registered name."}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {isIndividual ? (
              <>
                <div>
                  <label htmlFor="payee-last-name" className={tokenLabel}>Last name</label>
                  <input id="payee-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={tokenInput} disabled={busy} />
                </div>
                <div>
                  <label htmlFor="payee-first-name" className={tokenLabel}>First name</label>
                  <input id="payee-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={tokenInput} disabled={busy} />
                </div>
                <div>
                  <label htmlFor="payee-middle-name" className={tokenLabel}>Middle name</label>
                  <input id="payee-middle-name" value={middleName} onChange={(e) => setMiddleName(e.target.value)} className={tokenInput} disabled={busy} />
                </div>
              </>
            ) : (
              <div className="md:col-span-3">
                <label htmlFor="payee-registered-name" className={tokenLabel}>Registered name</label>
                <input
                  id="payee-registered-name"
                  value={registeredName}
                  onChange={(e) => setRegisteredName(e.target.value)}
                  className={tokenInput}
                  placeholder={name || "As shown on the BIR certificate of registration"}
                  disabled={busy}
                />
                <p className="text-xs text-ink-body mt-1">Leave blank to use the {term.lower} name.</p>
              </div>
            )}

            <div>
              <label htmlFor="payee-tin-branch" className={tokenLabel}>TIN branch code</label>
              <input
                id="payee-tin-branch"
                inputMode="numeric"
                maxLength={5}
                value={tinBranch}
                onChange={(e) => setTinBranch(onlyDigits(e.target.value))}
                className={tokenInput}
                placeholder="0000"
                disabled={busy}
              />
              <p className="text-xs text-ink-body mt-1">4 or 5 digits; blank uses the TIN's trailing digits.</p>
            </div>

            <label className="md:col-span-2 flex items-start gap-3 border border-ink-line rounded-md p-3 bg-white cursor-pointer self-start">
              <input
                type="checkbox"
                checked={isVatRegistered}
                onChange={(e) => setIsVatRegistered(e.target.checked)}
                className="h-4 w-4 mt-0.5 accent-primary-500"
                disabled={busy}
              />
              <span>
                <span className="block text-sm font-medium text-ink">VAT-registered</span>
                <span className="block text-xs text-ink-body mt-0.5">A VAT-registered individual always takes the higher EWT rate.</span>
              </span>
            </label>
          </div>

          <div className="mt-4 border border-ink-line rounded-md p-3 bg-ink-canvas">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasDeclaration}
                onChange={(e) => setHasDeclaration(e.target.checked)}
                className="h-4 w-4 mt-0.5 accent-primary-500"
                disabled={busy}
              />
              <span>
                <span className="block text-sm font-medium text-ink">Sworn declaration received</span>
                <span className="block text-xs text-ink-body mt-0.5">
                  Without one, the higher rate variant applies.
                </span>
              </span>
            </label>

            {hasDeclaration && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <label className="md:col-span-3 flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={belowThreshold}
                    onChange={(e) => setBelowThreshold(e.target.checked)}
                    className="h-4 w-4 mt-0.5 accent-primary-500"
                    disabled={busy}
                  />
                  <span className="text-sm text-ink">
                    {isIndividual
                      ? "Declares gross receipts of ₱3M or less (and not VAT-registered) — lower rate"
                      : "Declares gross income of ₱720K or less — lower rate"}
                  </span>
                </label>
                <div>
                  <label htmlFor="payee-declaration-year" className={tokenLabel}>Tax year</label>
                  <input
                    id="payee-declaration-year"
                    type="number"
                    min="2000"
                    max="2100"
                    value={declarationYear}
                    onChange={(e) => setDeclarationYear(e.target.value)}
                    className={tokenInput}
                    disabled={busy}
                  />
                </div>
                <div>
                  <label htmlFor="payee-declaration-date" className={tokenLabel}>Date received</label>
                  <input
                    id="payee-declaration-date"
                    type="date"
                    value={declarationDate}
                    onChange={(e) => setDeclarationDate(e.target.value)}
                    className={tokenInput}
                    disabled={busy}
                  />
                </div>
              </div>
            )}
          </div>
        </section>
        )}
      </div>

      {showButtons && (
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={busy}>
            {busy ? "Saving..." : isEditMode ? `Update ${term.one}` : `Create ${term.one}`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default VendorForm;
